import { type FC, useState } from "react";
import { type ActiveStorageBlob } from "react-activestorage";

import { useMutation } from "@apollo/client/react";

import { Transition } from "@headlessui/react";

import {
  type DocumentFragment,
  DocumentKindEnum,
  EvaluationTypeEnum,
  PriorReviewUploadDocument,
  type ProjectFragment,
  ProjectSetEvaluationTypeDocument,
  RecordReviewDisplayState,
  RecordReviewStatus,
  RunDocumentIntent,
  WorkflowRunDocument,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { toClientBuckets } from "@styled/billing_palette";

import type { AddRecordsChoice } from "./add_records_classify_overlay";
import type { FollowUpPriorInputKind } from "./follow_up_upload_stage";
import { ProcessRecordsStateView } from "./process_records_state_view";
import type { RecordDoc } from "./records_file_pills";
import { RsbActionButton, type RsbVariant } from "./records_status_bar";
import { describeFiles } from "./source_file_list";

// ------- Types ---------------------------------------------------------------

type RecordReview = {
  status: RecordReviewStatus;
  displayState: RecordReviewDisplayState;
  additionalRecordsCount: number;
  displayedPagesCount: number;
  pagesCount: number;
  processedPagesCount: number;
  lettersCount: number;
  reprocessable: boolean;
  canChangeEvaluationType: boolean;
  hasCompletedRun: boolean;
  hint: string;
  hintColor: string;
  title?: string | null;
  subtitle?: string | null;
  polling: boolean;
  run?: { id: string } | null;
  priorReviewDocument?: RecordDoc | null;
  baselineSourceDocuments?: RecordDoc[];
  additionalSourceDocuments?: RecordDoc[];
};

type ProcessRecordsCardProject = ProjectFragment & {
  recordReviewNewLetterCount?: number;
  recordReviewReviewedLetterCount?: number;
  recordReviewFilteredLetterCount?: number;
  recordReviewBaselineLetterCount?: number;
  recordReviewPageCount?: number;
  // Condensed-page billing composition (Page.pages_per_sheet > 1). All
  // values pre-computed server-side; UI reads directly.
  recordReviewTotalBillableUnits?: number;
  recordReviewNetBillableUnits?: number;
  recordReviewNonBillableUnits?: number;
  recordReviewFullPageCount?: number;
  recordReviewCondensedPageCount?: number;
  recordReviewCondensedBillableUnits?: number;
  recordReviewCondensedMultiplier?: number;
  recordReviewHasCondensedPages?: boolean;
  // Bar viz reads `pageCountBuckets` (raw page counts). `buckets`
  // (billable_units sums) is part of the GQL shape but unused here.
  recordReviewBilling?: {
    buckets: {
      baseline: number;
      previouslySeen: number;
      duplicate: number;
      nonEssential: number;
      unmatched: number;
    };
    pageCountBuckets: {
      baseline: number;
      previouslySeen: number;
      duplicate: number;
      nonEssential: number;
      unmatched: number;
    };
  } | null;
};

// Shared refetch options for the card's non-upload mutations.
const REFETCH_DETAILS = {
  refetchQueries: ["DashboardProjectsDetails"],
  awaitRefetchQueries: true,
};

// Uploads don't await the refetch: a failed reload after a committed save would
// mark the file "Upload failed", and re-dropping it creates a duplicate document.
const REFETCH_DETAILS_AFTER_UPLOAD = {
  refetchQueries: ["DashboardProjectsDetails"],
};

// D13: Supplemental / Follow-Up projects skip the classify picker and land
// directly on the upload form for their kind. On a Supplemental project the
// Add Records flow is "additional records into the existing supplemental" —
// no eval_type change. Comprehensive (and undefined) fall through to null
// so step 1 renders as usual.
const INITIAL_ADD_RECORDS_KIND: Partial<
  Record<EvaluationTypeEnum, AddRecordsChoice>
> = {
  [EvaluationTypeEnum.Supplemental]: "additional_records",
  [EvaluationTypeEnum.Followup]: "followup",
};

// Per D9: all three complete states share the green color treatment. The
// enum still distinguishes them so the state view can pick the right chip
// set and badge stack — only the bar color is unified.
const VARIANT_BY_STATE: Record<RecordReviewDisplayState, RsbVariant> = {
  [RecordReviewDisplayState.Idle]: "gray",
  [RecordReviewDisplayState.UploadingEmpty]: "none",
  [RecordReviewDisplayState.UploadingReady]: "indigo",
  [RecordReviewDisplayState.UploadingFollowUp]: "indigo",
  [RecordReviewDisplayState.Processing]: "active",
  [RecordReviewDisplayState.MergingFollowUp]: "active",
  [RecordReviewDisplayState.Complete]: "green",
  [RecordReviewDisplayState.CompleteWithFollowUp]: "green",
  [RecordReviewDisplayState.CompleteWithFailedRecord]: "green",
  [RecordReviewDisplayState.Failed]: "error",
};

export const ProcessRecordsCard: FC<{
  project: ProcessRecordsCardProject;
  documents: DocumentFragment[];
  recordReview?: RecordReview | null;
  workflow?: { id: string; parameters: { id: string }[] } | null;
  id: string;
  // Opens the page-level eval-type picker. `← Change` used to clear the type
  // on the server from here, which meant looking at your options changed the
  // case; the picker now opens with nothing committed until the user picks.
  onChangeEvalType: () => void;
}> = ({ project, documents, recordReview, workflow, id, onChangeEvalType }) => {
  const [setEvalType] = useMutation(ProjectSetEvaluationTypeDocument);
  const [runWorkflow] = useMutation(WorkflowRunDocument);
  const [uploadPriorReview] = useMutation(PriorReviewUploadDocument);

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [priorInputKind, setPriorInputKind] =
    useState<FollowUpPriorInputKind>("source_pdfs");

  const displayState =
    recordReview?.displayState ?? RecordReviewDisplayState.Idle;
  const variant = VARIANT_BY_STATE[displayState];
  const additionalRecordsCount = recordReview?.additionalRecordsCount ?? 0;
  const pages = recordReview?.displayedPagesCount ?? 0;
  const letters = recordReview?.lettersCount ?? 0;
  const locked = recordReview?.polling ?? false;

  const sourceFiles = documents.filter(
    (doc) => doc.kind === DocumentKindEnum.RecordReviewSource,
  );
  const firstFile = sourceFiles[0];
  const fileSummary = describeFiles(sourceFiles);
  // Server-resolved affordance: eval type can be changed until records exist
  // and processing is idle. See RecordReviewType#can_change_evaluation_type.
  const canChange = recordReview?.canChangeEvaluationType ?? true;

  // Promotes a Comprehensive project to Follow-Up when the Add Records wizard
  // uploads a follow-up batch (see handleAddRecordsUpload). The INITIAL type is
  // now chosen at the page-level setup gate, not in this pane.
  const handleSelectEvalType = async (type: EvaluationTypeEnum) => {
    await setEvalType({
      variables: { id: project.id, evaluationType: type },
      ...REFETCH_DETAILS,
    });
  };

  // Zone 2 / additional-records path. The server reads `intent: NewRecords`
  // and never touches Project#baseline_document_id.
  const handleUploadRecords = async (blob: ActiveStorageBlob) => {
    if (!workflow) return;
    await runWorkflow({
      variables: {
        workflowID: workflow.id,
        input: {
          projectID: project.id,
          fileID: blob.signed_id,
          intent: RunDocumentIntent.NewRecords,
        },
      },
      ...REFETCH_DETAILS_AFTER_UPLOAD,
    });
    track("record_processing_started", {
      project_id: project.id,
      intent: RunDocumentIntent.NewRecords,
    });
  };

  // Zone 1 Source-PDFs path. The server decides baseline assignment from
  // `intent: PriorRecords` + project state — first upload becomes baseline,
  // subsequent uploads in the same batch are added as record_review_source.
  // The frontend never reads project.baselineDocumentId; that's domain logic.
  const handleUploadPriorRecords = async (blob: ActiveStorageBlob) => {
    if (!workflow) return;
    await runWorkflow({
      variables: {
        workflowID: workflow.id,
        input: {
          projectID: project.id,
          fileID: blob.signed_id,
          intent: RunDocumentIntent.PriorRecords,
        },
      },
      ...REFETCH_DETAILS_AFTER_UPLOAD,
    });
    track("record_processing_started", {
      project_id: project.id,
      intent: RunDocumentIntent.PriorRecords,
    });
  };

  const handleUploadPriorReview = async (blob: ActiveStorageBlob) => {
    await uploadPriorReview({
      variables: {
        projectID: project.id,
        fileID: blob.signed_id,
        markAsBaseline: true,
      },
      ...REFETCH_DETAILS_AFTER_UPLOAD,
    });
    // Passive attach (no record-review run), so it emits document_uploaded —
    // not record_processing_started like the two record paths above.
    track("document_uploaded", {
      project_id: project.id,
      kind: "prior_review",
    });
  };

  const openAddRecordsOverlay = () => setOverlayOpen(true);
  const dismissOverlay = () => setOverlayOpen(false);

  // D13: Comprehensive projects see the classify step 1 (Additional Records vs
  // Follow-Up). Supplemental and Follow-Up projects skip step 1 and land
  // directly on the matching upload form for their kind.
  const initialAddRecordsKind = project.evaluationType
    ? (INITIAL_ADD_RECORDS_KIND[project.evaluationType] ?? null)
    : null;

  // D2: the wizard fires the eval-type mutation only when the user uploads a
  // file in step 2, not when they pick a choice in step 1 — this lets ← Change
  // return to step 1 without flipping the project's eval_type.
  //
  // Additional Records preserves eval_type regardless of the current project
  // type; only Follow-Up fires the Comp→Follow-Up promotion. Supplemental is
  // never set post-creation (see `ProjectSetEvaluationTypeMutation`).
  const handleAddRecordsUpload = async (
    choice: AddRecordsChoice,
    blob: ActiveStorageBlob,
  ) => {
    if (choice === "followup") {
      await handleSelectEvalType(EvaluationTypeEnum.Followup);
    }
    await handleUploadRecords(blob);
    setOverlayOpen(false);
  };

  const addRecordsButton = (
    <RsbActionButton
      onClick={openAddRecordsOverlay}
      disabled={locked}
      aria-label="Add records to this case"
      title={
        locked
          ? "Add Records is available once processing completes"
          : "Add additional records or start a follow-up evaluation"
      }
    >
      + Add Records
    </RsbActionButton>
  );

  // Load-bearing: changing this key forces Headless UI Transition to remount
  // and replay the enter animation on the new content.
  const transitionKey = overlayOpen ? "overlay" : displayState;

  return (
    <Transition
      key={transitionKey}
      as="div"
      appear
      show
      enter="transition-all duration-300 ease-out"
      enterFrom="opacity-0 translate-y-1"
      enterTo="opacity-100 translate-y-0"
    >
      <ProcessRecordsStateView
        context={{
          id,
          projectID: project.id,
          displayState,
          variant,
          pages,
          letters,
          additionalRecordsCount,
          canChange,
          fileSummary,
          evaluationType: project.evaluationType,
          sourceFiles,
          firstFile,
          recordReview,
          newLetterCount: project.recordReviewNewLetterCount ?? 0,
          reviewedLetterCount: project.recordReviewReviewedLetterCount ?? 0,
          filteredLetterCount: project.recordReviewFilteredLetterCount ?? 0,
          baselineLetterCount: project.recordReviewBaselineLetterCount ?? 0,
          totalPageCount: project.recordReviewPageCount ?? 0,
          // pageBuckets = raw page counts (drives the PagesSection bar
          // widths). The records-processed BillableSection on this surface
          // is headline-only (no bucket bars per Design QA correction 6),
          // so per-bucket billable_units sums aren't threaded here.
          pageBuckets: toClientBuckets(
            project.recordReviewBilling?.pageCountBuckets,
          ),
          // All composition values are server-provided — no derivation
          // happens here (CLAUDE.md "no business logic in the frontend").
          hasCondensedPages: project.recordReviewHasCondensedPages ?? false,
          nonBillableUnits: project.recordReviewNonBillableUnits ?? 0,
          netBillableUnits: project.recordReviewNetBillableUnits ?? 0,
          fullPageCount: project.recordReviewFullPageCount ?? 0,
          condensedPageCount: project.recordReviewCondensedPageCount ?? 0,
          condensedBillableUnits:
            project.recordReviewCondensedBillableUnits ?? 0,
          condensedMultiplier: project.recordReviewCondensedMultiplier ?? 1,
          priorInputKind,
          overlayOpen,
          initialAddRecordsKind,
          addRecordsButton,
          onChangeEvalType,
          onUploadRecords: handleUploadRecords,
          onUploadPriorRecords: handleUploadPriorRecords,
          onUploadPriorReview: handleUploadPriorReview,
          onPriorInputKindChange: setPriorInputKind,
          onAddRecordsUpload: handleAddRecordsUpload,
          onDismissOverlay: dismissOverlay,
        }}
      />
    </Transition>
  );
};
